// Kid home — "today's adventure". Fetches /kids/today (auto-enrolls a new kid),
// shows the next lesson as a big friendly card, and taps into the lesson player.
// Streak + XP chips reflect real values. docs/replan-v2.md R5.

import { useT } from '@fajrarabi/i18n/react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';

import { useAuth } from '~/lib/auth';
import { getToday } from '~/lib/learn';
import { asLocale } from '~/lib/locale';
import { listMyAssignments } from '~/lib/school';

export function KidHome() {
  const t = useT();
  const { user } = useAuth();
  const { locale: rawLocale } = useParams({ from: '/$locale' });
  const locale = asLocale(rawLocale);
  const name = user?.displayName ?? user?.username ?? '';

  const todayQuery = useQuery({
    queryKey: ['kids', 'today'],
    queryFn: ({ signal }) => getToday(signal),
    staleTime: 0,
  });

  const streak = todayQuery.data?.me.streak ?? 0;
  const xp = todayQuery.data?.me.xp ?? 0;
  const lesson = todayQuery.data?.lesson ?? null;
  const status = todayQuery.data?.status;
  const locked = todayQuery.data?.locked ?? false;

  // Assigned work (school students). Family kids get an empty list → section hides.
  const assignmentsQuery = useQuery({
    queryKey: ['kids', 'assignments'],
    queryFn: ({ signal }) => listMyAssignments(signal),
    staleTime: 30_000,
  });
  const assignments = assignmentsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-xl">
      <div className="mt-2 flex items-center gap-3">
        <span className="rounded-full bg-sun-300 px-4 py-1.5 text-sm font-bold text-burgundy-700">
          {t('app.kid.streak', { n: streak })}
        </span>
        <span className="rounded-full bg-leaf/30 px-4 py-1.5 text-sm font-bold text-burgundy-700">
          {t('app.kid.xp', { n: xp })}
        </span>
      </div>

      <h1 className="mt-6 text-3xl font-bold text-burgundy-700">
        {t('app.kid.greeting', { name })}
      </h1>

      <div className="mt-6">
        {todayQuery.isLoading ? (
          <div className="h-72 animate-pulse rounded-[2rem] bg-cream-200" />
        ) : todayQuery.isError ? (
          <PlanCard emoji="😴" title={t('app.kid.todays_plan')} body={t('app.kid.load_error')} />
        ) : lesson && locked ? (
          // Next lesson is behind the paywall — show an upgrade card (the CTA
          // links to billing so a parent on the kid's device can subscribe).
          <PlanCard
            emoji="🔒"
            title={t('app.paywall.locked_title')}
            body={t('app.paywall.locked_body')}
            cta={
              <Link
                to="/$locale/billing"
                params={{ locale }}
                className="mt-6 inline-flex min-h-[56px] items-center justify-center rounded-full bg-burgundy-500 px-12 text-lg font-bold text-cream-50 transition-colors hover:bg-burgundy-600"
              >
                {t('app.paywall.parent_unlock')}
              </Link>
            }
          />
        ) : lesson ? (
          <PlanCard
            emoji="📚"
            title={t('app.kid.todays_plan')}
            body={t('app.kid.lesson_minutes', { n: lesson.estMinutes })}
            cta={
              <Link
                to="/$locale/lesson/$lessonId"
                params={{ locale, lessonId: lesson.id }}
                className="mt-6 inline-flex min-h-[56px] items-center justify-center rounded-full bg-burgundy-500 px-12 text-lg font-bold text-cream-50 transition-colors hover:bg-burgundy-600"
              >
                {status === 'in_progress'
                  ? t('app.kid.continue_lesson')
                  : t('app.kid.start_lesson')}
              </Link>
            }
          />
        ) : (
          <PlanCard
            emoji="🎈"
            title={t('app.kid.all_done_title')}
            body={t('app.kid.all_done_body')}
          />
        )}
      </div>

      {assignments.length > 0 && (
        <div className="mt-8">
          <h2 className="text-xl font-bold text-burgundy-700">{t('app.kid.assigned_title')}</h2>
          <ul className="mt-3 space-y-2">
            {assignments.map((a) => (
              <li
                key={a.id}
                className="flex items-center justify-between rounded-2xl bg-white p-4 ring-2 ring-sun-300/30"
              >
                <span className="font-semibold text-ink-900">{t(a.lessonTitleKey)}</span>
                {a.completed ? (
                  <span className="rounded-full bg-leaf/25 px-3 py-1 text-sm font-bold text-leaf">
                    ✓ {t('app.kid.assigned_done')}
                  </span>
                ) : (
                  <Link
                    to="/$locale/lesson/$lessonId"
                    params={{ locale, lessonId: a.lessonId }}
                    className="rounded-full bg-burgundy-500 px-5 py-2 text-sm font-bold text-cream-50 hover:bg-burgundy-600"
                  >
                    {t('app.kid.assigned_start')}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PlanCard({
  emoji,
  title,
  body,
  cta,
}: {
  emoji: string;
  title: string;
  body: string;
  cta?: React.ReactNode;
}) {
  return (
    <div className="rounded-[2rem] bg-gradient-to-b from-sun-50 to-cream-50 p-8 text-center shadow-[0_24px_60px_-30px_rgba(31,26,23,0.25)] ring-2 ring-sun-300/40">
      <div className="text-6xl" aria-hidden="true">
        {emoji}
      </div>
      <h2 className="mt-4 text-2xl font-bold text-ink-900">{title}</h2>
      <p className="mx-auto mt-2 max-w-xs text-ink-700">{body}</p>
      {cta}
    </div>
  );
}
